/**
 * Observe an element's size and run `onResize` (debounced) when it changes to a new, non-zero size.
 *
 * Chart components use this to keep a Highcharts instance sized to its container via `chart.setSize(...)`
 * on any layout change (window resize, dashboard module resize, sibling reflows), rather than recreating the
 * chart from window dimensions — which caused blank/partial renders when many charts re-rendered at once
 * against a transient container size.
 *
 * @param element The container element to observe (typically the chart's render/host element).
 * @param onResize Called with the rounded, non-zero content width/height once the size settles.
 * @param options.debounceMs How long the size must settle before `onResize` runs (default 20ms).
 * @param options.skip Optional predicate; when it returns true the resize is ignored (e.g. for charts that
 *                     size themselves from the window rather than their container).
 * @returns The ResizeObserver, so the caller can `disconnect()` it on destroy.
 */
export function observeContainerResize(element, onResize, options = {}) {
    const debounceMs = options.debounceMs ?? 20;
    let resizeTimeout = null;
    const observer = new ResizeObserver((entries) => {
        if (options.skip?.()) {
            return;
        }
        const width = Math.round(entries[0].contentRect.width);
        const height = Math.round(entries[0].contentRect.height);
        // Never resize to a zero-size container — that renders a blank chart.
        if (width === 0 || height === 0) {
            return;
        }
        if (resizeTimeout) {
            clearTimeout(resizeTimeout);
        }
        resizeTimeout = setTimeout(() => onResize(width, height), debounceMs);
    });
    observer.observe(element);
    return observer;
}
